import axios from 'axios'
import {
  setTokens,
  getAccessToken,
  getRefreshToken,
  clearTokens,
  ineErrorHandler,
} from '@/helpers'
import { BUSINESS, PROFILE, UAA } from '@/clients'
import CONSTANTS from '@/constants'

const loginUser = async formData => {
  const { email: username, password, recaptcha: captchaToken } = formData
  const route = `users/authenticate`
  const postData = {
    username,
    password,
    captchaToken,
  }
  try {
    const {
      data: { data },
    } = await UAA.post(route, postData)
    return data
  } catch (error) {
    throw ineErrorHandler(error)
  }
}

const fetchStatus = async () => {
  const route = `auth/state/status`
  return UAA.get(route)
    .then(response => Promise.resolve(response.data.data))
    .catch(error => Promise.reject(ineErrorHandler(error)))
}

const fetchNewToken = async () => {
  const token = getAccessToken()
  const refresh_token = getRefreshToken()
  const route = `${process.env.VUE_APP_UAA_URL}auth/refresh-token`
  await axios
    .post(
      route,
      { refresh_token },
      {
        headers: {
          Accept: 'application/json',
          common: {
            'Content-Type': 'application/json; charset=utf-8',
          },
          post: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then(async response => {
      if (response?.data?.data?.tokens?.data) {
        await setTokens(response.data.data.tokens.data)
      }
      return response.data.data
    })
    .catch(async error => {
      await clearTokens()
      return error
    })
}

const logoutUser = async () => {
  const route = `logout`
  return UAA.post(route, {})
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(ineErrorHandler(error)))
}

const postMFACode = async code => {
  const route = `users/mfa/validate`
  return UAA.post(route, { code })
    .then(response => Promise.resolve(response.data.data))
    .catch(error => Promise.reject(ineErrorHandler(error)))
}

const fetchMFACode = async () => {
  const route = `users/mfa/resend`
  return UAA.post(route, {})
    .then(response => Promise.resolve(response.data.data))
    .catch(error => Promise.reject(ineErrorHandler(error)))
}

const fetchAuthProviders = async () => {
  const route = `providers?referrer=my-ine`
  return UAA.get(route)
    .then(response => Promise.resolve(response.data.data))
    .catch(error => Promise.reject(ineErrorHandler(error)))
}

const fetchSampleAvatars = async () => {
  const route = `users/avatar/default?number=all`
  return PROFILE.get(route)
    .then(response => Promise.resolve(response.data.data))
    .catch(error => Promise.reject(ineErrorHandler(error)))
}

const postUserAvatar = async fileAndType => {
  const { file, type } = fileAndType

  const formData = new FormData()
  switch (type) {
    case 'binary':
      formData.append('user_avatar', file)
      break
    case 'base64':
      formData.append('base64_avatar', file)
      break
    default:
      formData.append('user_avatar', file)
      break
  }

  const route = `users/avatar`
  return PROFILE.post(route, formData)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(ineErrorHandler(error)))
}

const postConfirmNewAccount = async codeAndUUID => {
  const { code: confirmation_code, username } = codeAndUUID
  const postData = { confirmation_code, username }
  const route = `users/confirm`
  return UAA.post(route, postData)
    .then(response => Promise.resolve(response.data.data))
    .catch(error => Promise.reject(error))
}

const postForgotPassword = async email => {
  const route = `users/reset-password`
  return UAA.post(route, { email })
    .then(response => Promise.resolve(response.data.data))
    .catch(error => Promise.reject(ineErrorHandler(error)))
}

const postNewPasswordByCode = async formData => {
  const { code: confirmation_code, username, password } = formData
  const postData = {
    confirmation_code,
    username,
    password,
    confirm_password: password,
  }
  const route = `users/reset-password/confirm`
  return UAA.post(route, postData)
    .then(response => Promise.resolve(response.data.data))
    .catch(error => Promise.reject(ineErrorHandler(error)))
}

const postChangePassword = async currentAndNewPasswords => {
  const {
    currentPassword: old_password,
    password: new_password,
  } = currentAndNewPasswords
  const route = `users/change-password`
  return UAA.post(route, { old_password, new_password })
    .then(response => Promise.resolve(response.data.data))
    .catch(error => Promise.reject(ineErrorHandler(error)))
}

const postChangePhoneNumber = async phone_number => {
  const route = `users/phone`
  return PROFILE.post(route, { phone_number })
    .then(response => Promise.resolve(response.data.data))
    .catch(error => Promise.reject(ineErrorHandler(error)))
}

const postChangeMFAMethod = async method => {
  const route = `users/change-mfa`
  return UAA.post(route, { method })
    .then(response => Promise.resolve(response.data.data))
    .catch(error => Promise.reject(ineErrorHandler(error)))
}

const fetchUserPreferences = async () => {
  const route = `users/preferences`

  try {
    const {
      data: { data },
    } = await PROFILE.get(route)
    return data
  } catch (error) {
    throw ineErrorHandler(error)
  }
}

const putUserPreferences = async preferences => {
  const route = `users/preferences`
  try {
    const {
      data: { data },
    } = await PROFILE.put(route, preferences)
    return data
  } catch (error) {
    throw ineErrorHandler(error)
  }
}

const fetchUserWithSAQuestEmbed = async () => {
  const route = `users/self?embed=${CONSTANTS.SECURITY_AWARENESS.QUEST_SLUG}`
  try {
    const {
      data: { data },
    } = await BUSINESS.get(route)
    return data
  } catch (error) {
    throw ineErrorHandler(error)
  }
}

export {
  loginUser,
  fetchStatus,
  fetchNewToken,
  logoutUser,
  postMFACode,
  fetchMFACode,
  fetchAuthProviders,
  fetchSampleAvatars,
  postUserAvatar,
  postConfirmNewAccount,
  postForgotPassword,
  postNewPasswordByCode,
  postChangePassword,
  postChangePhoneNumber,
  postChangeMFAMethod,
  fetchUserPreferences,
  putUserPreferences,
  fetchUserWithSAQuestEmbed,
}
